import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, Box, Typography, IconButton, ToggleButtonGroup, ToggleButton, CircularProgress, Alert,
  Select, MenuItem, FormControl,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { canvasVersionsAPI } from '../../../../services/api';
import { diffCanvases, buildSlotNameMap } from './agentDiff';
import { DiffNavContext } from './renderDiffText';
import NodeDiffList from './NodeDiffList';
import EdgeDiffList from './EdgeDiffList';

export default function DiffOverlay({ open, onClose, agentName, liveNodes, liveEdges, versions, initialVersionId }) {
  const [selectedVersionId, setSelectedVersionId] = useState(initialVersionId || null);
  const [viewMode, setViewMode] = useState('inline');
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedNodeIds, setExpandedNodeIds] = useState(() => new Set());
  const [jumpTargetIds, setJumpTargetIds] = useState(() => new Set());
  const [pendingScrollId, setPendingScrollId] = useState(null);
  const nodeRefs = useRef(new Map());

  useEffect(() => {
    if (open) {
      setSelectedVersionId(initialVersionId || null);
    } else {
      setSelectedVersionId(null);
      setSnapshot(null);
      setError(null);
    }
  }, [open, initialVersionId]);

  useEffect(() => {
    if (!selectedVersionId) return;
    setLoading(true);
    setError(null);
    canvasVersionsAPI
      .get(agentName, selectedVersionId)
      .then((data) => setSnapshot(data))
      .catch(() => setError('Failed to load that version.'))
      .finally(() => setLoading(false));
  }, [agentName, selectedVersionId]);

  const diff = useMemo(
    () =>
      snapshot
        ? diffCanvases({ nodes: liveNodes, edges: liveEdges }, { nodes: snapshot.canvas_nodes, edges: snapshot.canvas_edges })
        : null,
    [snapshot, liveNodes, liveEdges]
  );

  // Whatever actually changed starts expanded. jumpTargetIds is unrelated to
  // this default and only tracks nodes a jump-link has explicitly visited, so
  // it resets separately -- otherwise every changed row (expanded by default)
  // would count as a "jump target" and permanently force its group open.
  useEffect(() => {
    if (!diff) return;
    setExpandedNodeIds(new Set(diff.nodes.filter((n) => n.status !== 'unchanged').map((n) => n.id)));
    setJumpTargetIds(new Set());
  }, [diff]);

  const slotMap = useMemo(
    () => (snapshot ? buildSlotNameMap(liveNodes, snapshot.canvas_nodes) : new Map()),
    [snapshot, liveNodes]
  );

  const jumpToNode = (nodeId) => {
    setExpandedNodeIds((prev) => new Set(prev).add(nodeId));
    setJumpTargetIds((prev) => new Set(prev).add(nodeId));
    setPendingScrollId(nodeId);
  };

  // Runs after React commits the expand-state update, so the target is
  // actually laid out before we scroll to it. Instant (not smooth) scroll --
  // smooth-scroll animation is throttled/skipped while the tab isn't visible.
  useEffect(() => {
    if (!pendingScrollId) return;
    nodeRefs.current.get(pendingScrollId)?.scrollIntoView({ behavior: 'auto', block: 'start' });
    setPendingScrollId(null);
  }, [pendingScrollId, expandedNodeIds]);

  const registerNodeRef = (nodeId, el) => {
    if (el) nodeRefs.current.set(nodeId, el);
    else nodeRefs.current.delete(nodeId);
  };

  // Old-then-new so a node present in both resolves to its current (live) label.
  const nodesById = new Map([...(snapshot?.canvas_nodes || []), ...liveNodes].map((n) => [n.id, n]));

  return (
    <Dialog fullScreen open={open} onClose={onClose} PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Diff: {agentName}</Typography>
        {/* Read-only picker -- never calls canvasVersionsAPI.activate. */}
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <Select value={selectedVersionId || ''} displayEmpty onChange={(e) => setSelectedVersionId(e.target.value)} sx={{ fontSize: 13 }}>
            <MenuItem value="" disabled sx={{ fontSize: 13 }}>Pick a version to diff against…</MenuItem>
            {[...versions].reverse().map((v) => (
              <MenuItem key={v.id} value={v.id} sx={{ fontSize: 13 }}>
                {v.label || v.created_at?.slice(0, 16) || v.id}{v.is_active ? ' ✓' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ToggleButtonGroup size="small" exclusive value={viewMode} onChange={(_, v) => v && setViewMode(v)}>
          <ToggleButton value="inline" sx={{ fontSize: 12, py: 0.25 }}>Inline</ToggleButton>
          <ToggleButton value="side-by-side" sx={{ fontSize: 12, py: 0.25 }}>Side-by-side</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose} size="small" aria-label="Close diff">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
        {!selectedVersionId && !loading && (
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Pick a version above to diff it against your current (unsaved) canvas.
          </Typography>
        )}
        {loading && <CircularProgress size={20} />}
        {error && <Alert severity="error">{error}</Alert>}
        {diff && !loading && (
          <DiffNavContext.Provider value={{ slotMap, jumpToNode }}>
            <NodeDiffList
              nodes={diff.nodes}
              viewMode={viewMode}
              expandedNodeIds={expandedNodeIds}
              jumpTargetIds={jumpTargetIds}
              onToggleNode={(id) =>
                setExpandedNodeIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id);
                  else next.add(id);
                  return next;
                })
              }
              registerNodeRef={registerNodeRef}
            />
            <EdgeDiffList edges={diff.edges} nodesById={nodesById} />
          </DiffNavContext.Provider>
        )}
      </Box>
    </Dialog>
  );
}
